import { formatToTinyFrenchNumber, getSymbolFromCurrency } from '~~/shared/utils/format'

const allCurrencyFormatters = new Map<string, Intl.NumberFormat>()

/**
 * Format a currency value to the fr-FR currency format
 *
 * @export
 * @param {number} value
 * @param {string} [currency='USD']
 * @return {*}  {string}
 */
export function formatCurrency(value: number, currency: string = 'USD', options?: Partial<Intl.NumberFormatOptions>): string {
  // Create a new formatter if it doesn't exist
  const lang = 'fr-FR'
  const formatterHash = JSON.stringify({ lang, currency, options })
  if (!allCurrencyFormatters.has(formatterHash)) {
    allCurrencyFormatters.set(formatterHash, new Intl.NumberFormat(lang, {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
      currencyDisplay: (currency === 'USD') ? 'narrowSymbol' : 'symbol',
      ...options
    }))
  }

  // Formatting the value
  let formattedValue = allCurrencyFormatters.get(formatterHash)!.format(value)

  // If the value was 0 and the formatted value starts with "-", removing the "-" (without deprecated substr)
  if (value === 0 && formattedValue.startsWith('-')) {
    formattedValue = formattedValue.replace('-', '')
  }

  // If there is a "k" or "M" in the formatted value, removing the space before it
  if (formattedValue.includes("\xa0k")) {
    formattedValue = formattedValue.replace("\xa0k", 'k')
  } else if (formattedValue.includes("\xa0M")) {
    formattedValue = formattedValue.replace('\xa0M', 'M')
  }

  // Return the formatted value
  return formattedValue
}

/**
 * Format a currency value to the fr-FR currency format with two digits
 *
 * @export
 * @param {...Parameters<typeof formatCurrency>} args
 * @return {*}  {ReturnType<typeof formatCurrency>}
 */
export function formatCurrencyWithDigits(...args: Parameters<typeof formatCurrency>): ReturnType<typeof formatCurrency> {
  // Changing the options to force the number of digits to two
  if (!args[2]) {
    args[2] = {}
  }
  args[2].minimumFractionDigits = 2
  args[2].maximumFractionDigits = 2

  // Return the formatted value
  return formatCurrency(...args)
}

export function formatRoundedCurrency(value: number, currency: string = 'USD'): string {
  if (!Number.isFinite(value)) {
    return formatCurrency(value, currency)
  }

  const absoluteValue = Math.abs(value)
  const roundedValue = absoluteValue > 0 && Math.trunc(absoluteValue) === 0
    ? Math.round(value * 10) / 10
    : Math.round(value)

  return formatCurrency(roundedValue, currency, {
    minimumFractionDigits: absoluteValue > 0 && Math.trunc(absoluteValue) === 0 ? 1 : 0,
    maximumFractionDigits: absoluteValue > 0 && Math.trunc(absoluteValue) === 0 ? 1 : 0,
  })
}

export function formatTinyCurrency(value: number, currency: string = 'USD', decimalPlaces?: number): string {
  if (!Number.isFinite(value)) {
    return formatCurrency(value, currency)
  }

  // Let the compact formatter select its precision after converting to k / M / Mds.
  // Rounding the raw amount first would turn 2.2 billion into 2 billion.
  return `${formatToTinyFrenchNumber(value, decimalPlaces)} ${getSymbolFromCurrency(currency)}`
}

type RoceTooltipAmountSourceUnit = 'millions' | 'absolute'

/**
 * Format a ROCE tooltip amount without decimals, using the current display unit convention.
 *
 * @export
 * @param {number} value
 * @param {string} [currency='USD']
 * @param {RoceTooltipAmountSourceUnit} [sourceUnit='millions']
 * @return {*}  {string}
 */
export function formatRoceTooltipAmount(
  value: number | null | undefined,
  currency: string = 'USD',
  sourceUnit: RoceTooltipAmountSourceUnit = 'millions'
): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return '∅'
  }

  const valueInMillions = sourceUnit === 'absolute' ? value / 1_000_000 : value
  const shouldDisplayInBillions = Math.abs(valueInMillions) >= 1_000
  const normalizedValue = shouldDisplayInBillions ? valueInMillions / 1_000 : valueInMillions
  const unitLabel = shouldDisplayInBillions ? 'Mds' : 'M'

  const roundedValue = threeDigitMinimum(normalizedValue)
  const decimalPlaces = Math.abs(roundedValue) < 10 ? 2 : 0
  const formattedValue = new Intl.NumberFormat('fr-FR', {
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces,
  }).format(roundedValue)

  return `${formattedValue} ${unitLabel} ${getSymbolFromCurrency(currency)}`
}

/**
 * Convert a currency code to its symbol
 *
 * @export
 * @param {string} currencyCode
 * @return {*}  {string}
 */
export function convertCurrencyCodeToSymbol(currencyCode: string): string {
  const formatter = new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: currencyCode,
    currencyDisplay: 'symbol',
  })
  return formatter.format(0).replace(/0/g, '').replace(',', '').replace(/\s/g, '').trim()
}

/**
 * Format a number to have at least 3 numbers, so 123.34 become 123, 12.34 becomes 12.3, 1.34 becomes 1.34
 *
 * @export
 * @param {number} value
 * @return {*}  {number}
 */
export function threeDigitMinimum(value: number): number {
  if (value >= 100) {
    // For numbers >= 100: round to nearest integer (no decimals)
    return Math.round(value)
  } else if (value >= 10) {
    // For numbers >= 10 and < 100: 1 decimal place
    return Math.round(value * 10) / 10
  } else {
    // For numbers < 10: 2 decimal places
    return Math.round(value * 100) / 100
  }
}
